<script>
	import { translate } from "$lib/utilities/translate";
	import { slugify } from "cando-util";

	import MarkedContent from "$lib/components/MarkedContent.svelte";
	import PhoneNumber from "$lib/components/PhoneNumber.svelte";
	import Textarea from "$lib/components/Textarea.svelte";
	import QuestionOptionInput from "$lib/components/QuestionOptionInput.svelte";
	import FileUploads from "$lib/components/FormSubmission/FileUploads.svelte";
	import Signature from "$lib/components/FormSubmission/Signature.svelte";
	import Select from "$lib/components/Select.svelte";
	import { getPreviewQuestionSpanClass } from "./previewLayout.js";
	import Templates from "$lib/components/Templates/Templates.svelte";

	export let question;
	export let response;
	export let lang;
	export let handleChange;
	export let responseIsValid;
	export let required;
	export let readonly;
	export let disabled;
	export let hideInForm;
	export let hideIframe = false;
	export let previewColumns;

	let isValid; // we're really not using this but the component needs to bind to something.

	$: label = translate(question.label, lang);
	$: hideLabel = question.hideLabel?.value === "true" ? true : false;
	$: colSpan = question.colSpan?.value || "1";
	$: heading = translate(question.heading, lang);
	$: invalidMessage = translate(question.invalidMessage, lang) || "";
	$: accept =
		question.accept?.value === undefined
			? undefined
			: (question.accept?.value || "").split("+||+");
	$: info = translate(question.info, lang);
</script>

<div
	class={`${hideInForm ? "hidden" : getPreviewQuestionSpanClass(colSpan, previewColumns)} w-full flex flex-col justify-start`}
>
	<div class="w-full flex-none block">
		{#if heading !== ""}
			<heading class="flex gap-4">
				<MarkedContent
					markedContent={heading}
					class={question.headingClass?.value}
				/>
				{#if (question.required?.value === "true" || required) && !readonly && !disabled}
					<div
						class={question.headingClass?.value ||
							"marked-content documents"}
					>
						<p class="text-error">*</p>
					</div>
				{/if}

				<!-- Leaves room for the invalid message and gives us some space so we don't need a bottom margin for heading content -->
				{#if question.label?.value}
					<span class="hidden sm:block">&nbsp;</span>
				{/if}
			</heading>
		{/if}

		{#if responseIsValid === false}
			<div class="text-error">
				{invalidMessage}&nbsp
			</div>
		{/if}

		{#if question.type?.value === "phone" || question.type?.value === "tel"}
			<PhoneNumber
				bind:value={response}
				bind:isValid
				label={question.label.value}
				handleChange={() => handleChange(question)}
				{readonly}
				{disabled}
				key={question.label.value}
			/>
		{:else if ["checkbox", "radio"].includes(question.type?.value)}
			<div>
				<QuestionOptionInput
					label={`${question.label.value}`}
					optionLayout={question.optionLayout?.value}
					labelClass="text-lg sm:text-xl"
					bind:value={response}
					options={(question.options?.value || "").split("+||+")}
					type={question.type?.value}
					optionLabelPos={question.optionLabelPos?.value}
					handleChange={() => handleChange(question)}
					{readonly}
					{disabled}
				/>
			</div>
		{:else if ["dropdown", "select"].includes(question.type?.value.toLowerCase())}
			<Select
				bind:value={response}
				name={`${question.label.value}`}
				key={question.label.value}
				options={(question.options?.value || "").replace(
					/\+\|\|\+/g,
					"\n",
				)}
				handleChange={() => handleChange(question)}
				{readonly}
				{disabled}
			/>
		{:else if question.type?.value === "textarea"}
			<Textarea
				label={question.label.value}
				additionalClass="text-lg sm:text-xl"
				bind:value={response}
				handleInputChange={() => handleChange(question)}
				keepTab={false}
				{readonly}
				{disabled}
			/>
		{:else if question.type?.value === "file"}
			<FileUploads
				bind:value={response}
				{question}
				handleChange={() => handleChange(question)}
				{label}
				{readonly}
				{accept}
			/>
		{:else if question.type?.value === "signature"}
			<Signature
				bind:value={response}
				{question}
				handleChange={() => handleChange(question)}
				{label}
				{readonly}
				{disabled}
			/>
		{:else if question.type?.value === "date"}
			<input
				id={`${slugify(question.label.value)}-${question.type?.value}-input`}
				class={`w-full input input-bordered text-lg sm:text-xl`}
				type="date"
				name={question.label.value}
				bind:value={response}
				{required}
				on:change={() => handleChange(question)}
				{readonly}
				{disabled}
			/>
		{:else if question.type?.value === "time"}
			<input
				id={`${slugify(question.label.value)}-${question.type?.value}-input`}
				class={`w-full input input-bordered text-lg sm:text-xl`}
				type="time"
				name={question.label.value}
				bind:value={response}
				{required}
				on:change={() => handleChange(question)}
				{readonly}
				{disabled}
			/>
		{:else if question.type?.value === "templates"}
			<Templates
				bind:value={response}
				{question}
				{handleChange}
				{readonly}
				{label}
				{hideIframe}
			/>
		{:else if ["text", "email"].includes(question.type?.value)}
			<input
				id={`${slugify(question.label.value)}-${question.type?.value}-input`}
				class={`w-full input input-bordered text-lg sm:text-xl`}
				type={question.type?.value}
				name={question.label.value}
				bind:value={response}
				{required}
				on:change={() => handleChange(question)}
				{readonly}
				{disabled}
			/>
		{/if}
		<div class="flex gap-2 sm:gap-4">
			{#if !hideLabel && label}
				<div>
					{label}
				</div>
				{#if (question.required?.value === "true" || required) && !readonly && !disabled}
					<span class="text-error">*</span>
				{/if}
			{/if}
			{#if info}
				<div>{info}</div>
			{/if}
		</div>
	</div>
</div>
